import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import loanReceivedService from '../../services/LoanReceivedService.js';
import { Camera } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const AddLoanReceived = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    lender_type: '',
    lender_name: '',
    lender_contact: '',
    lender_email: '',
    lender_address: '',
    account_number: '',
    ifsc_code: '',
    loan_type: '',
    loan_amount: '',
    interest_rate: '0',
    processing_fee: '0',
    loan_date: new Date().toISOString().split('T')[0],
    due_date: '',
    disbursed_date: '',
    repayment_frequency: '',
    total_installments: '1',
    installment_amount: '0',
    loan_account_number: '',
    collateral_details: '',
    purpose: '',
    remarks: '',
    auto_approve: false
  });
  
  const [loanAgreement, setLoanAgreement] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [loanTypes, setLoanTypes] = useState([]);
  const [lenderTypes, setLenderTypes] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const navigate = useNavigate();

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    lender_type:        (v) => validators.select(v, 'lender type'),
    lender_name:        (v) => validators.required(v, 'Lender name'),
    lender_contact:     (v) => validators.required(v, 'Contact') || validators.contact(v),
    lender_email:       (v) => v ? validators.email(v) : '',
    account_number:     (v) => v ? (/^\d+$/.test(v) ? '' : 'Account number must contain only digits') : '',
    ifsc_code:          (v) => v ? validators.ifsc(v) : '',
    loan_type:          (v) => validators.select(v, 'loan type'),
    loan_amount:        (v) => validators.required(v, 'Loan amount') || validators.number(v, { min: 0.01, label: 'Loan amount' }),
    interest_rate:      (v) => v ? validators.number(v, { min: 0, max: 100, label: 'Interest rate' }) : '',
    processing_fee:     (v) => v ? validators.number(v, { min: 0, label: 'Processing fee' }) : '',
    loan_date:          (v) => validators.required(v, 'Loan date'),
    due_date:           (v) => validators.required(v, 'Due date'),
    repayment_frequency:(v) => validators.select(v, 'repayment frequency'),
    purpose:            (v) => validators.required(v, 'Purpose'),
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    calculateTotalAmount();
  }, [formData.loan_amount, formData.interest_rate, formData.processing_fee, formData.loan_date, formData.due_date]);

  const fetchData = async () => {
    try {
      const [typesRes, lenderTypesRes, freqRes] = await Promise.all([
        loanReceivedService.getLoanTypes(),
        loanReceivedService.getLenderTypes(),
        loanReceivedService.getFrequencies()
      ]);
      
      setLoanTypes(typesRes.data || []);
      setLenderTypes(lenderTypesRes.data || []);
      setFrequencies(freqRes.data || []);
    } catch (err) {
      Alert.error('Failed to load data');
      console.error(err);
    }
  };

  const calculateTotalAmount = () => {
    const principal = parseFloat(formData.loan_amount) || 0;
    const rate = parseFloat(formData.interest_rate) || 0;
    const fee = parseFloat(formData.processing_fee) || 0;
    
    if (principal > 0 && rate > 0 && formData.loan_date && formData.due_date) {
      const startDate = new Date(formData.loan_date);
      const endDate = new Date(formData.due_date);
      const days = Math.max(0, (endDate - startDate) / (1000 * 60 * 60 * 24));
      const interest = (principal * rate * days) / (365 * 100);
      setTotalAmount(principal + interest + fee);
    } else {
      setTotalAmount(principal + fee);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let filtered = type === 'checkbox' ? checked : value;
    if (name === 'lender_contact') filtered = inputFilters.digits10(value);
    if (name === 'loan_amount' || name === 'interest_rate' || name === 'processing_fee') filtered = inputFilters.amount(value);
    if (name === 'account_number') filtered = inputFilters.digitsOnly(value);
    if (name === 'ifsc_code') filtered = value.toUpperCase();

    setFormData(prev => ({
      ...prev,
      [name]: filtered
    }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleDocumentChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      setLoanAgreement(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setDocumentPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setDocumentPreview('pdf');
      }
    }
  };

  const removeDocument = () => {
    setLoanAgreement(null);
    setDocumentPreview(null);
    const fileInput = document.getElementById('agreement-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    if (formData.due_date && formData.loan_date && new Date(formData.due_date) <= new Date(formData.loan_date)) {
      Alert.error('Due date must be after loan date');
      return;
    }
    
    setLoading(true);

    try {
      await loanReceivedService.create(formData, loanAgreement);
      Alert.success('Loan record created successfully!');
      setTimeout(() => navigate('/loans-received'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create loan');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <Camera className="w-6 h-6 text-buttonBg" /> Add Loan Received
                  </h1>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
                  {/* Section 1: Lender Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Lender Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Lender Type <span class="text-red-500">*</span>
                        </label>
                        <SelectField variant="form"
                          name="lender_type"
                          value={formData.lender_type}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.lender_type ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select Type</option>
                          {lenderTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.lender_type} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Lender Name <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="lender_name"
                          value={formData.lender_name}
                          onChange={handleChange}
                          placeholder="Enter lender name"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.lender_name ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.lender_name} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Contact Number <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="lender_contact"
                          value={formData.lender_contact}
                          onChange={handleChange}
                          placeholder="Enter contact number"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.lender_contact ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.lender_contact} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          name="lender_email"
                          value={formData.lender_email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.lender_email ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.lender_email} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Account Number
                        </label>
                        <input
                          type="text"
                          name="account_number"
                          value={formData.account_number}
                          onChange={handleChange}
                          placeholder="Enter account number"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.account_number ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.account_number} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          IFSC Code
                        </label>
                        <input
                          type="text"
                          name="ifsc_code"
                          value={formData.ifsc_code}
                          onChange={handleChange}
                          placeholder="Enter IFSC code"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.ifsc_code ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.ifsc_code} />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Address
                        </label>
                        <textarea
                          name="lender_address"
                          value={formData.lender_address}
                          onChange={handleChange}
                          placeholder="Enter full address"
                          rows="2"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Loan Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Loan Details
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Type <span class="text-red-500">*</span>
                        </label>
                        <SelectField variant="form"
                          name="loan_type"
                          value={formData.loan_type}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.loan_type ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select Type</option>
                          {loanTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.loan_type} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Amount <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="loan_amount"
                          value={formData.loan_amount}
                          onChange={handleChange}
                          placeholder="Enter loan amount"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.loan_amount ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.loan_amount} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Interest Rate (% per annum)
                        </label>
                        <input
                          type="text"
                          name="interest_rate"
                          value={formData.interest_rate}
                          onChange={handleChange}
                          placeholder="Enter interest rate"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.interest_rate ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.interest_rate} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Processing Fee
                        </label>
                        <input
                          type="text"
                          name="processing_fee"
                          value={formData.processing_fee}
                          onChange={handleChange}
                          placeholder="Enter processing fee"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.processing_fee ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.processing_fee} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Total Amount (with Interest & Fee)
                        </label>
                        <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base font-semibold text-green-600">
                          {formatCurrency(totalAmount)}
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Account Number
                        </label>
                        <input
                          type="text"
                          name="loan_account_number"
                          value={formData.loan_account_number}
                          onChange={handleChange}
                          placeholder="Enter loan account number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Date <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="loan_date"
                          value={formData.loan_date}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.loan_date ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.loan_date} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Due Date <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="due_date"
                          value={formData.due_date}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.due_date ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.due_date} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Disbursed Date
                        </label>
                        <input
                          type="date"
                          name="disbursed_date"
                          value={formData.disbursed_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Repayment Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Repayment Details
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Repayment Frequency <span class="text-red-500">*</span>
                        </label>
                        <SelectField variant="form"
                          name="repayment_frequency"
                          value={formData.repayment_frequency}
                          onChange={handleChange}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.repayment_frequency ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        >
                          <option value="">Select Frequency</option>
                          {frequencies.map((freq) => (
                            <option key={freq.value} value={freq.value}>
                              {freq.label}
                            </option>
                          ))}
                        </SelectField>
                        <ValidationMessage error={errors.repayment_frequency} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Total Installments
                        </label>
                        <input
                          type="number"
                          name="total_installments"
                          value={formData.total_installments}
                          onChange={handleChange}
                          placeholder="Enter total installments"
                          min="1"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Installment Amount
                        </label>
                        <input
                          type="number"
                          name="installment_amount"
                          value={formData.installment_amount}
                          onChange={handleChange}
                          placeholder="Enter installment amount"
                          step="0.01"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Additional Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Additional Information
                    </h3>
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Purpose <span class="text-red-500">*</span>
                        </label>
                        <textarea
                          name="purpose"
                          value={formData.purpose}
                          onChange={handleChange}
                          placeholder="Enter the purpose of the loan"
                          rows="3"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.purpose ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.purpose} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Collateral Details
                        </label>
                        <textarea
                          name="collateral_details"
                          value={formData.collateral_details}
                          onChange={handleChange}
                          placeholder="Enter collateral details (if any)"
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Agreement Document
                        </label>
                       <MediaCaptureField mode="document" onPick={handleDocumentChange} />

<label
  htmlFor="agreement-input"
  className="w-40 h-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
>
  {!documentPreview ? (
    <div className="text-center">
      <Camera className="mx-auto w-8 h-8 text-gray-400 mb-2" />
      <p className="text-xs text-gray-500">Click to upload</p>
    </div>
  ) : documentPreview === 'pdf' ? (
    <div
      className="flex flex-col items-center justify-center text-center px-2"
      onClick={(e) => e.preventDefault()}
    >
      <span className="text-2xl mb-2">📄</span>
      <p className="text-xs text-gray-600 break-all">
        {loanAgreement?.name}
      </p>
    </div>
  ) : (
    <img
      src={documentPreview}
      alt="Document"
      className="w-full h-full object-contain rounded-lg"
      onClick={(e) => e.preventDefault()}
    />
  )}

  {documentPreview && (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        removeDocument();
      }}
      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
    >
      ✕
    </button>
  )}
</label>

<p className="text-xs text-gray-500 mt-2">
  Maximum file size: 5MB. Supported formats: JPG, PNG, PDF
</p>
                          
                        
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Remarks
                        </label>
                        <textarea
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleChange}
                          placeholder="Enter any additional remarks"
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            name="auto_approve"
                            checked={formData.auto_approve}
                            onChange={handleChange}
                            className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-blue-400"
                          />
                          <span className="text-gray-700">Auto-approve this loan</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                    <Button type="submit" variant="primary" disabled={loading} loading={loading}>
                      {loading ? 'Creating...' : 'Create Loan'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/loans-received')}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default AddLoanReceived;